import React, { useState, useEffect, useContext, lazy, Suspense, memo, useRef, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GlobalPlayerProvider } from '@/context/GlobalPlayerContext';
import { useGlobalPlayer } from '@/context/GlobalPlayerContext';
import { AuthProvider, AuthContext } from '@/context/AuthContext';
import { PremiumUpgradeProvider } from '@/context/PremiumUpgradeContext';
import { useAuth } from '@/hooks/useAuth';
import TopBar from '@/components/TopBar';
import SplashScreen from '@/components/SplashScreen';
import MiniPlayer from '@/components/GlobalPlayer/MiniPlayer';
import Navbar from '@/components/Navbar';
import SmartRedirect from '@/components/SmartRedirect';
import ScrollToTop from '@/components/ScrollToTop';
import GlobalPlayer from '@/components/GlobalPlayer/GlobalPlayer';
import { useSongData } from '@/hooks/useSongData';
import { App as CapacitorApp } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import ToastProvider from '@/components/ToastProvider';
import { useNoOverscroll } from '@/hooks/useNoOverscroll';
import { useAppLifecycle } from '@/hooks/useAppLifecycle';
import { clearCacheData } from '@/services/cleanupService';
import { PERFORMANCE_CONFIG, getAdaptiveTimeout } from '@/config/performance';
import timeoutManager from '@/utils/timeoutManager';
import { Capacitor } from '@capacitor/core';
import { setupNavigationService } from '@/services/navigationService';
import SupabaseStatusBanner from '@/components/SupabaseStatusBanner';
import PromoBanner from '@/components/PromoBanner';
import { testConnection } from '@/lib/supabase';

// Componente simplificado para redirecionar /musica/:id para /player?id=:id
const MusicRedirect = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (id && /^\d+$/.test(id)) {
      navigate(`/player?id=${id}`, { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  }, [id, navigate]);
  
  return <SplashScreen />;
};

// Lazy loading das páginas
const Home = lazy(() => import('@/pages/Index'));
const Player = lazy(() => import('@/pages/PlayerPage'));
const WebPlayer = lazy(() => import('@/pages/WebPlayer'));
const Categorias = lazy(() => import('@/pages/Categorias'));
const Caminhada = lazy(() => import('@/pages/Caminhada'));
const Favoritos = lazy(() => import('@/pages/Favoritos'));
const Settings = lazy(() => import('@/pages/Settings'));
const Auth = lazy(() => import('@/pages/auth'));
const AuthCallback = lazy(() => import('@/pages/auth/callback'));
const SignupPage = lazy(() => import('@/pages/Signup'));

const SobreOApp = lazy(() => import('@/pages/SobreOApp'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));

// Admin pages
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminSongsList = lazy(() => import('@/pages/admin/AdminSongsList'));
const AdminSongForm = lazy(() => import('@/pages/admin/AdminSongForm'));
const TermsOfUse = lazy(() => import('@/pages/TermsOfUse'));
const Success = lazy(() => import('@/pages/Success'));

// QueryClient otimizado
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 30 * 60 * 1000, // 30 minutos
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Evita refetch ao montar
      refetchOnReconnect: false // Evita refetch ao reconectar
    },
  },
});

// Componente para verificação inicial
const InitialCheck = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  
  useEffect(() => {
    const checkInitialRoute = async () => {
      try {
        // Aguarda o carregamento da autenticação terminar
        if (loading) return;
        
        // Se o usuário está autenticado, vai para home
        // Se não está autenticado, vai para auth
        if (user) {
          navigate('/home', { replace: true });
        } else {
          navigate('/auth', { replace: true });
        }
      } catch (error) {
        console.error('Erro ao verificar rota inicial:', error);
        // Em caso de erro, redireciona baseado no estado atual do usuário
        navigate(user ? '/home' : '/auth', { replace: true });
      } finally {
        setIsChecking(false);
      }
    };

    checkInitialRoute();
  }, [navigate, user, loading]);

  if (isChecking || loading) {
    return <SplashScreen />;
  }

  return null;
};

// Componente memoizado para evitar re-renders desnecessários
const AppContent = memo(() => {
  const { user, loading: authLoading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // 🚀 OTIMIZAÇÃO: Memoiza valores que dependem apenas de location.pathname
  const shouldHideNavbar = useMemo(() => {
    const path = location.pathname;
    return ['/auth', '/auth/callback', '/signup', '/landing', '/privacy-policy', '/terms-of-use'].includes(path) 
      || path.startsWith('/web-player') 
      || path.startsWith('/admin');
  }, [location.pathname]);
  
  const shouldHideTopBar = useMemo(() => {
    const path = location.pathname;
    return ['/auth', '/auth/callback', '/signup', '/landing', '/privacy-policy', '/terms-of-use'].includes(path) 
      || path.startsWith('/web-player') 
      || path.startsWith('/admin');
  }, [location.pathname]);
  
  const [isPreloadingImages, setIsPreloadingImages] = useState(true);
  
  useAppLifecycle();

  // Configura o serviço de navegação
  useEffect(() => {
    setupNavigationService(navigate);
  }, [navigate]);
  
  useNoOverscroll();

  return (
    <div className="app-container bg-background min-h-screen">
      <Suspense fallback={<SplashScreen />}>
      {location.pathname === '/' ? (
        <InitialCheck />
      ) : location.pathname.startsWith('/web-player') ? (
        <Routes>
          <Route path="/web-player/:id" element={<WebPlayer />} />
        </Routes>
      ) : (
        <>
          {!shouldHideTopBar && <TopBar />}
          <PromoBanner />
          
          <main className="main-content bg-background min-h-screen">
            <Routes>
              <Route path="/" element={<SplashScreen />} />
              <Route path="/home" element={<Home />} />
              <Route path="/player" element={<Player />} />
              <Route path="/player/:id" element={<Player />} />
              <Route path="/musica/:id" element={<SmartRedirect />} />
              <Route path="/categorias" element={<Categorias />} />
              <Route path="/jornada" element={<Caminhada />} />
              <Route path="/caminhada" element={<Caminhada />} />
              <Route path="/caminhada/:stageId" element={<Caminhada />} />
              <Route path="/favoritos" element={<Favoritos />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/sobre-o-app" element={<SobreOApp />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/success" element={<Success />} />
              {/* Admin Routes */}
              <Route
                path="/admin"
                element={
                  <AdminLayout>
                    <AdminSongsList />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/songs/new"
                element={
                  <AdminLayout>
                    <AdminSongForm />
                  </AdminLayout>
                }
              />
              <Route
                path="/admin/songs/:id/edit"
                element={
                  <AdminLayout>
                    <AdminSongForm />
                  </AdminLayout>
                }
              />
            </Routes>
          </main>
          
          {!shouldHideNavbar && <Navbar />}
          <MiniPlayer />
        </>
      )}
      </Suspense>
    </div>
  );
});

const App = () => {
  const [hasError, setHasError] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Limpeza inicial
        await clearCacheData();
        
        // Testa conexão ao montar o componente
        await testConnection();
        
        // Marca o app como pronto
        setIsAppReady(true);
        
        // Limpeza periódica
        const interval = setInterval(() => {
          clearCacheData().catch(() => {
            // Ignora erros assíncronos
          });
        }, 5 * 60 * 1000); // 5 minutos
        
        return () => clearInterval(interval);
      } catch (error) {
        console.error('Erro ao inicializar app:', error);
        setHasError(true);
        // Tenta novamente após um delay
        setTimeout(() => {
          setHasError(false);
          setIsAppReady(true);
        }, 2000);
      }
    };

    initializeApp();
  }, []);

  // Fallback de erro
  if (hasError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center p-6">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground">Inicializando aplicativo...</p>
        </div>
      </div>
    );
  }

  // Aguarda inicialização
  if (!isAppReady) {
    return <SplashScreen />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <PremiumUpgradeProvider>
            <GlobalPlayerProvider>
              <ToastProvider>
                <AppContent />
                <GlobalPlayer />
                <ScrollToTop />
                <SupabaseStatusBanner />
              </ToastProvider>
            </GlobalPlayerProvider>
          </PremiumUpgradeProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default memo(App);
